import { getCell, inBounds } from '../domain/pattern';
import type { Cell, Pattern } from '../domain/types';

/** A cell about to be painted. Tools emit these; the store applies them. */
export interface Op {
  col: number;
  row: number;
  value: number;
}

export type Plot = (col: number, row: number) => void;

export function brushAt(col: number, row: number, size: number, plot: Plot): void {
  if (size <= 1) {
    plot(col, row);
    return;
  }
  const r = Math.floor(size / 2);
  for (let dy = -r; dy <= r; dy++) {
    for (let dx = -r; dx <= r; dx++) {
      // Round brush reads better than a square one at small sizes.
      if (size > 2 && dx * dx + dy * dy > r * r + 0.5) continue;
      plot(col + dx, row + dy);
    }
  }
}

export function line(a: Cell, b: Cell, plot: Plot): void {
  let x0 = a.col;
  let y0 = a.row;
  const x1 = b.col;
  const y1 = b.row;
  const dx = Math.abs(x1 - x0);
  const dy = -Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  for (;;) {
    plot(x0, y0);
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) {
      err += dy;
      x0 += sx;
    }
    if (e2 <= dx) {
      err += dx;
      y0 += sy;
    }
  }
}

export function rectangle(a: Cell, b: Cell, filled: boolean, plot: Plot): void {
  const x0 = Math.min(a.col, b.col);
  const x1 = Math.max(a.col, b.col);
  const y0 = Math.min(a.row, b.row);
  const y1 = Math.max(a.row, b.row);
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      if (filled || y === y0 || y === y1 || x === x0 || x === x1) plot(x, y);
    }
  }
}

export function ellipse(a: Cell, b: Cell, filled: boolean, plot: Plot): void {
  const x0 = Math.min(a.col, b.col);
  const x1 = Math.max(a.col, b.col);
  const y0 = Math.min(a.row, b.row);
  const y1 = Math.max(a.row, b.row);
  const cx = (x0 + x1) / 2;
  const cy = (y0 + y1) / 2;
  const rx = Math.max(0.5, (x1 - x0) / 2);
  const ry = Math.max(0.5, (y1 - y0) / 2);
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      const d = ((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2;
      if (filled ? d <= 1.02 : d <= 1.02 && d >= 0.55) plot(x, y);
    }
  }
}

/**
 * Four-connected flood fill over the grid.
 *
 * Staggered stitches have diagonal physical neighbours, but filling by grid
 * adjacency is what every chart editor does and is what users predict, so the
 * grid — not the bead geometry — defines connectivity here.
 */
export function floodFill(p: Pattern, start: Cell, plot: Plot): void {
  const target = getCell(p, start.col, start.row);
  const seen = new Uint8Array(p.width * p.height);
  const stack: number[] = [start.row * p.width + start.col];
  while (stack.length) {
    const i = stack.pop()!;
    if (seen[i]) continue;
    seen[i] = 1;
    const col = i % p.width;
    const row = (i - col) / p.width;
    if (p.cells[i] !== target) continue;
    plot(col, row);
    if (col > 0) stack.push(i - 1);
    if (col < p.width - 1) stack.push(i + 1);
    if (row > 0) stack.push(i - p.width);
    if (row < p.height - 1) stack.push(i + p.width);
  }
}

export interface Symmetry {
  h: boolean;
  v: boolean;
}

/**
 * Wrap a plot function so every stroke is echoed across the mirror axes.
 * Mirroring uses the grid centre, so on a staggered stitch the reflected bead
 * is the one in the mirrored column, which is what a beader wants.
 */
export function withSymmetry(p: Pattern, sym: Symmetry, plot: Plot): Plot {
  if (!sym.h && !sym.v) return plot;
  return (col, row) => {
    plot(col, row);
    const mc = p.width - 1 - col;
    const mr = p.height - 1 - row;
    if (sym.h) plot(mc, row);
    if (sym.v) plot(col, mr);
    if (sym.h && sym.v) plot(mc, mr);
  };
}

export function clipped(p: Pattern, plot: Plot): Plot {
  return (col, row) => {
    if (inBounds(p, col, row)) plot(col, row);
  };
}

export function collectOps(p: Pattern, value: number, run: (plot: Plot) => void): Op[] {
  const seen = new Set<number>();
  const ops: Op[] = [];
  run(
    clipped(p, (col, row) => {
      const key = row * p.width + col;
      if (seen.has(key)) return;
      seen.add(key);
      ops.push({ col, row, value });
    }),
  );
  return ops;
}
